import { toast_error } from '@/libs/toast-wrappers';
import { errorlog } from '@/libs/error-logs';
import {
  ERROR_FETCH_URGENT_CASES_DASHBOARD,
  ERROR_FETCH_CASE_COMMENTS,
  ERROR_POST_CASE_COMMENT,
  ERROR_PATCH_CASE_COMMENT,
  ERROR_DELETE_CASE_COMMENT,
  ERROR_FETCH_CLINIC_CASE_ADMINS,
} from '@/utils/messages';
import { toError } from '@/utils/errors';

import { DELETE, GET, POST } from './base/index';
import { PATCH } from './base/base-request';
import { ENDPOINTS } from './endpoints';
import { StandardResponse } from './standard-response';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Response from GET /grades/urgent-cases-dashboard
 */
export type UrgentCasesDashboardResponse = {
  total_count: number;
  unacknowledged_count: number;
  cases: UrgentCaseDashboardItem[];
};

/**
 * A single urgent case item from the dashboard endpoint
 */
export type UrgentCaseDashboardItem = {
  grade_id: string;
  case_id: string;
  patient_id: string;
  clinic_id: string;
  clinic_name: string;
  is_urgent_case: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
  case_admins: { email: string; full_name: string; contact_phone: string }[];
};

/**
 * Query parameters for GET /grades/urgent-cases-dashboard
 */
export type GetUrgentCasesDashboardParams = {
  urgent_acknowledged?: boolean;
  clinic_id?: string;
};

/**
 * A case comment record from GET/POST /cases/{case_id}/comments
 */
export type CaseComment = {
  comment_id: string;
  case_id: string;
  user_email: string;
  user_role: string;
  content: string;
  created_at: string;
  updated_at: string | null;
  is_internal: boolean;
};

/**
 * Payload for POST /cases/{case_id}/comments
 */
export type PostCaseCommentPayload = {
  content: string;
  is_internal?: boolean;
};

/**
 * Payload for PATCH /cases/{case_id}/comments/{comment_id}
 */
export type PatchCaseCommentPayload = {
  content: string;
};

/**
 * A Case Admin contact from GET /clinics/{clinic_id}/case-admins
 */
export type CaseAdminContact = {
  email: string;
  role: 'CaseAdmin';
};

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetches the urgent cases dashboard with optional filtering
 * @param params - Optional filters (urgent_acknowledged, clinic_id)
 * @returns Dashboard data with total counts and case list
 */
export async function getUrgentCasesDashboard(
  params?: GetUrgentCasesDashboardParams
): Promise<StandardResponse<UrgentCasesDashboardResponse>> {
  try {
    const query: Record<string, string | undefined> = {};

    if (params?.urgent_acknowledged !== undefined) {
      query.urgent_acknowledged = String(params.urgent_acknowledged);
    }
    if (params?.clinic_id) {
      query.clinic_id = params.clinic_id;
    }

    const response = await GET<StandardResponse<UrgentCasesDashboardResponse>>(
      ENDPOINTS.URGENT_CASES_DASHBOARD,
      { query }
    );
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/urgent-cases/getUrgentCasesDashboard',
      'Error fetching urgent cases dashboard'
    );
    toast_error(ERROR_FETCH_URGENT_CASES_DASHBOARD);
    return Promise.reject(toError(error));
  }
}

/**
 * Fetches all comments for a case
 * @param caseId - The case ID to fetch comments for
 * @returns List of case comments
 */
export async function getCaseComments(caseId: string): Promise<StandardResponse<CaseComment[]>> {
  try {
    const response = await GET<StandardResponse<CaseComment[]>>(ENDPOINTS.CASE_COMMENTS, {
      parameters: { caseId },
    });
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/urgent-cases/getCaseComments',
      'Error fetching case comments'
    );
    toast_error(ERROR_FETCH_CASE_COMMENTS);
    return Promise.reject(toError(error));
  }
}

/**
 * Adds a comment to a case
 * @param caseId - The case ID to comment on
 * @param payload - The comment content and optional is_internal flag
 * @returns The created comment
 */
export async function postCaseComment(
  caseId: string,
  payload: PostCaseCommentPayload
): Promise<StandardResponse<CaseComment>> {
  try {
    const response = await POST<StandardResponse<CaseComment>>(ENDPOINTS.CASE_COMMENTS, payload, {
      parameters: { caseId },
    });
    return response;
  } catch (error) {
    errorlog(error as Error, 'src/api/urgent-cases/postCaseComment', 'Error posting case comment');
    toast_error(ERROR_POST_CASE_COMMENT);
    return Promise.reject(toError(error));
  }
}

/**
 * Updates an existing comment
 * @param caseId - The case ID the comment belongs to
 * @param commentId - The comment ID to update
 * @param payload - The updated comment content
 * @returns The updated comment
 */
export async function patchCaseComment(
  caseId: string,
  commentId: string,
  payload: PatchCaseCommentPayload
): Promise<StandardResponse<CaseComment>> {
  try {
    const response = await PATCH<StandardResponse<CaseComment>>(ENDPOINTS.CASE_COMMENT, payload, {
      parameters: { caseId, commentId },
    });
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/urgent-cases/patchCaseComment',
      'Error updating case comment'
    );
    toast_error(ERROR_PATCH_CASE_COMMENT);
    return Promise.reject(toError(error));
  }
}

/**
 * Deletes a comment
 * @param caseId - The case ID the comment belongs to
 * @param commentId - The comment ID to delete
 */
export async function deleteCaseComment(
  caseId: string,
  commentId: string
): Promise<StandardResponse<null>> {
  try {
    const response = await DELETE<StandardResponse<null>>(ENDPOINTS.CASE_COMMENT, {
      parameters: { caseId, commentId },
    });
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/urgent-cases/deleteCaseComment',
      'Error deleting case comment'
    );
    toast_error(ERROR_DELETE_CASE_COMMENT);
    return Promise.reject(toError(error));
  }
}

/**
 * Fetches Case Admin contacts for a clinic
 * @param clinicId - The clinic ID to get case admins for
 * @returns List of Case Admin contacts
 */
export async function getClinicCaseAdmins(
  clinicId: string
): Promise<StandardResponse<CaseAdminContact[]>> {
  try {
    const response = await GET<StandardResponse<CaseAdminContact[]>>(ENDPOINTS.CLINIC_CASE_ADMINS, {
      parameters: { clinicId },
    });
    return response;
  } catch (error) {
    errorlog(
      error as Error,
      'src/api/urgent-cases/getClinicCaseAdmins',
      'Error fetching clinic case admins'
    );
    toast_error(ERROR_FETCH_CLINIC_CASE_ADMINS);
    return Promise.reject(toError(error));
  }
}
